import React, { useEffect, useState } from 'react';
import { Heading, Typography } from '../common/Typography';
import { Card } from '../common/Card';
import Button from '../common/Button';
import { Modal, ModalContent, ModalCloseButton, ConfirmationModal } from '../common';
import { FormField, Input, Textarea } from '../common/Form';
import { ManagementActionRow } from '../clubs/ManagementHubSection';
import {
  archiveSessionSeries,
  createSessionSeries,
  listSessionSeries,
  updateSessionSeries,
} from '../../services/sessionSeriesService';

const emptyForm = { name: '', description: '' };

const SessionSeriesSection = ({ selectedClub }) => {
  const [series, setSeries] = useState([]);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [archiveTarget, setArchiveTarget] = useState(null);

  const fetchSeries = async () => {
    if (!selectedClub?.id) return;
    try {
      setError(null);
      const rows = await listSessionSeries(selectedClub.id, { includeInactive: true });
      setSeries(rows);
    } catch (err) {
      console.error('Error fetching session series:', err);
      setError(err.message || 'Failed to load session series');
    }
  };

  useEffect(() => {
    fetchSeries();
  }, [selectedClub?.id]);

  const closeModal = () => {
    setShowModal(false);
    setEditing(null);
    setForm(emptyForm);
  };

  const openCreate = () => {
    setEditing(null);
    setForm(emptyForm);
    setShowModal(true);
    setSuccess(null);
    setError(null);
  };

  const openEdit = (row) => {
    setEditing(row);
    setForm({ name: row.name || '', description: row.description || '' });
    setShowModal(true);
    setSuccess(null);
    setError(null);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setLoading(true);
    setError(null);
    setSuccess(null);
    try {
      if (editing) {
        await updateSessionSeries(editing.id, form);
        setSuccess('Series updated.');
      } else {
        await createSessionSeries({
          clubId: selectedClub.id,
          name: form.name,
          description: form.description,
        });
        setSuccess('Series created.');
      }
      await fetchSeries();
      closeModal();
    } catch (err) {
      setError(err.message || 'Failed to save series');
    } finally {
      setLoading(false);
    }
  };

  const handleArchive = async () => {
    if (!archiveTarget) return;
    setLoading(true);
    setError(null);
    try {
      await archiveSessionSeries(archiveTarget.id);
      setSuccess(`Archived ${archiveTarget.name}. Past sessions keep this series.`);
      setArchiveTarget(null);
      await fetchSeries();
    } catch (err) {
      setError(err.message || 'Failed to archive series');
    } finally {
      setLoading(false);
    }
  };

  if (!selectedClub) return null;

  const activeSeries = series.filter((row) => row.is_active !== false);
  const archivedSeries = series.filter((row) => row.is_active === false);

  return (
    <div className="space-y-2">
      {success ? (
        <Card variant="success" size="small">
          <Typography variant="body" className="text-text-primary">{success}</Typography>
        </Card>
      ) : null}
      {error ? (
        <Card variant="danger" size="small">
          <Typography variant="body" className="text-text-primary">{error}</Typography>
        </Card>
      ) : null}

      {activeSeries.map((row) => (
        <ManagementActionRow
          key={row.id}
          iconType="planning"
          title={row.name}
          description={row.description || 'Standing series'}
          onClick={() => openEdit(row)}
          actions={
            <>
              <Button onClick={() => openEdit(row)} variant="outline" size="small">
                Edit
              </Button>
              <Button
                onClick={() => setArchiveTarget(row)}
                variant="outline"
                size="small"
                className="border-danger text-danger hover:bg-danger-light"
              >
                Archive
              </Button>
            </>
          }
        />
      ))}
      {archivedSeries.map((row) => (
        <ManagementActionRow
          key={row.id}
          iconType="archive"
          title={`${row.name} (archived)`}
          description={row.description || 'Hidden from new sessions'}
          actions={<span className="text-xs text-text-muted">Archived</span>}
        />
      ))}
      <ManagementActionRow
        iconType="planning"
        title={activeSeries.length === 0 ? 'Create series' : 'Add series'}
        onClick={openCreate}
      />

      <Modal isOpen={showModal} onClose={closeModal}>
        <ModalContent size="medium" padding={false} className="overflow-hidden">
          <div className="border-b border-border-medium p-4 sm:p-6">
            <div className="flex items-start justify-between gap-2 pr-8">
              <Heading level={2}>{editing ? 'Edit series' : 'Create series'}</Heading>
              <ModalCloseButton onClose={closeModal} />
            </div>
          </div>
          <form onSubmit={handleSubmit} className="space-y-4 p-4 sm:p-6">
            <FormField label="Series name" required helperText="Standing night this date belongs to, e.g. Classic Tuesday.">
              <Input
                value={form.name}
                onChange={(event) => setForm((prev) => ({ ...prev, name: event.target.value }))}
                placeholder="e.g. Classic Tuesday"
              />
            </FormField>
            <FormField label="Description">
              <Textarea
                value={form.description}
                onChange={(event) => setForm((prev) => ({ ...prev, description: event.target.value }))}
                placeholder="Optional"
                rows={3}
              />
            </FormField>
            <div className="flex justify-end gap-2">
              <Button type="button" variant="outline" onClick={closeModal} disabled={loading}>
                Cancel
              </Button>
              <Button type="submit" variant="primary" disabled={loading}>
                {loading ? 'Saving…' : editing ? 'Save series' : 'Create series'}
              </Button>
            </div>
          </form>
        </ModalContent>
      </Modal>

      <ConfirmationModal
        isOpen={Boolean(archiveTarget)}
        onClose={() => setArchiveTarget(null)}
        onConfirm={handleArchive}
        title="Archive series"
        message={
          archiveTarget
            ? `Archive ${archiveTarget.name}? Existing sessions keep this series. It will no longer appear when creating new sessions.`
            : ''
        }
        confirmText="Archive"
        cancelText="Keep series"
        isDangerous
      />
    </div>
  );
};

export default SessionSeriesSection;
